import React, { useState, useEffect } from 'react';
import { authFetch } from '../services/api';
import TemplateGallery from './TemplateGallery';
import RepoPickerModal from './RepoPickerModal';
import messageIcon from '../message.png';
import codingIcon from '../programming.png';
import rocketIcon from '../rocket.png';

const TYPING_PHRASES = [
  'a landing page for ...',
  'an internal tool that ...',
  'an application that can ...',
  'a dashboard for managing ...',
  'a website that helps users ...',
  'a platform where users can ...',
];

function useTypingAnimation() {
  const [displayed, setDisplayed] = React.useState('');
  const [phraseIdx, setPhraseIdx] = React.useState(0);
  const [isDeleting, setIsDeleting] = React.useState(false);

  React.useEffect(() => {
    const current = TYPING_PHRASES[phraseIdx];
    const typeSpeed = isDeleting ? 40 : 70;
    const pauseDelay = !isDeleting && displayed === current ? 1600 : typeSpeed;

    const timer = setTimeout(() => {
      if (!isDeleting && displayed === current) {
        setIsDeleting(true);
      } else if (isDeleting && displayed === '') {
        setIsDeleting(false);
        setPhraseIdx(i => (i + 1) % TYPING_PHRASES.length);
      } else {
        setDisplayed(prev =>
          isDeleting ? prev.slice(0, -1) : current.slice(0, prev.length + 1)
        );
      }
    }, pauseDelay);

    return () => clearTimeout(timer);
  }, [displayed, isDeleting, phraseIdx]);

  return displayed;
}

const FRAMEWORK_COLORS = {
  react:   { bg: 'rgba(97,218,251,0.1)',  color: '#61dafb' },
  vue:     { bg: 'rgba(66,184,131,0.1)',  color: '#42b883' },
  angular: { bg: 'rgba(221,0,49,0.1)',    color: '#dd0031' },
  vanilla: { bg: 'rgba(247,223,30,0.1)',  color: '#f7df1e' },
  node:    { bg: 'rgba(104,160,99,0.1)',  color: '#68a063' },
};

function frameworkStyle(fw) {
  const f = fw?.toLowerCase();
  return FRAMEWORK_COLORS[f] || { bg: 'rgba(99,102,241,0.12)', color: 'var(--accent)' };
}

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

// ── Featured UI Preview ─────────────────────────────────────────────────────
// Larger, richer SVG mockup for the featured card's 260px-wide preview pane.
// Same type inference as MiniPreview — pure SVG, no APIs, no images.

function FeaturedPreview({ name, gradient }) {
  const type = inferPreviewType(name);
  const accentMatch = gradient.match(/rgba\((\d+),(\d+),(\d+)/);
  const ac = accentMatch ? `rgb(${accentMatch[1]},${accentMatch[2]},${accentMatch[3]})` : '#6366f1';
  const W = 280, H = 160;
  const line  = 'rgba(255,255,255,0.11)';
  const surf  = 'rgba(255,255,255,0.07)';
  const surf2 = 'rgba(255,255,255,0.04)';
  const txt   = 'rgba(255,255,255,0.28)';
  const txt2  = 'rgba(255,255,255,0.14)';
  const txt3  = 'rgba(255,255,255,0.08)';
  const p = {  // shared svgProps
    width: '100%', height: '100%',
    viewBox: `0 0 ${W} ${H}`,
    xmlns: 'http://www.w3.org/2000/svg',
    style: { display: 'block' },
    preserveAspectRatio: 'xMidYMid slice',
  };

  if (type === 'ecommerce') return (
    <svg {...p}>
      {/* navbar */}
      <rect width={W} height="22" fill={surf} />
      <rect x="10" y="7" width="36" height="8" rx="2" fill={ac} opacity="0.5" />
      <rect x="180" y="8" width="22" height="6" rx="2" fill={txt2} />
      <rect x="208" y="8" width="22" height="6" rx="2" fill={txt2} />
      <rect x="244" y="6" width="26" height="10" rx="3" fill={ac} opacity="0.35" />
      {/* 3 product cards */}
      {[0,1,2].map(i => (
        <g key={i} transform={`translate(${8 + i * 90}, 30)`}>
          <rect width="82" height="118" rx="6" fill={surf} stroke={line} strokeWidth="0.7" />
          {/* product image area */}
          <rect x="6" y="6" width="70" height="52" rx="4"
            fill={i === 1 ? ac : surf2} opacity={i === 1 ? '0.28' : '1'} />
          {/* shimmer line on image */}
          <rect x="6" y="6" width="70" height="52" rx="4"
            fill="url(#imgSheen)" opacity="0.18" />
          {/* title */}
          <rect x="8" y="64" width="52" height="6" rx="2" fill={txt} />
          <rect x="8" y="74" width="36" height="4" rx="2" fill={txt2} />
          {/* price */}
          <rect x="8" y="84" width="28" height="7" rx="2" fill={ac} opacity="0.55" />
          {/* add to cart */}
          <rect x="8" y="98" width="66" height="14" rx="4" fill={i===1?ac:surf2}
            opacity={i===1?'0.45':'1'} stroke={i===1?'none':line} strokeWidth="0.5" />
          <rect x="22" y="102" width="38" height="5" rx="2"
            fill={i===1?'rgba(255,255,255,0.6)':txt2} />
        </g>
      ))}
      <defs>
        <linearGradient id="imgSheen" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="white" stopOpacity="0.3" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );

  if (type === 'dashboard') return (
    <svg {...p}>
      {/* sidebar */}
      <rect width="52" height={H} fill={surf} />
      <rect x="10" y="12" width="32" height="8" rx="2" fill={ac} opacity="0.45" />
      {[0,1,2,3,4].map(i => (
        <rect key={i} x="10" y={28 + i * 18} width={i===0?32:24} height="6" rx="2"
          fill={i===0?ac:txt2} opacity={i===0?'0.35':'1'} />
      ))}
      {/* top metric cards */}
      {[0,1,2].map(i => (
        <g key={i} transform={`translate(${60 + i * 74}, 8)`}>
          <rect width="68" height="36" rx="5" fill={surf} stroke={line} strokeWidth="0.6" />
          <rect x="6" y="7" width="20" height="5" rx="2" fill={txt2} />
          <rect x="6" y="17" width="36" height="9" rx="2"
            fill={i===0?ac:txt} opacity={i===0?'0.5':'1'} />
          <rect x="6" y="29" width="24" height="3" rx="1.5" fill={txt3} />
        </g>
      ))}
      {/* bar chart */}
      {[38,24,52,30,58,20,44,34].map((h,i) => (
        <rect key={i} x={60 + i * 16} y={H - 14 - h} width="11" height={h} rx="2"
          fill={i===4?ac:surf} opacity={i===4?'0.55':'1'} stroke={line} strokeWidth="0.4" />
      ))}
      {/* line chart */}
      <polyline
        points="192,110 206,92 220,100 234,76 248,84 262,62 276,70"
        fill="none" stroke={ac} strokeWidth="2" strokeOpacity="0.65"
        strokeLinecap="round" strokeLinejoin="round" />
      <polyline
        points="192,110 206,92 220,100 234,76 248,84 262,62 276,70 276,148 192,148"
        fill={ac} fillOpacity="0.07" stroke="none" />
      {/* x-axis */}
      <line x1="60" y1={H-14} x2={W-4} y2={H-14} stroke={line} strokeWidth="0.5" />
    </svg>
  );

  if (type === 'restaurant') return (
    <svg {...p}>
      {/* hero image */}
      <rect width={W} height="56" fill={surf2} />
      <rect width={W} height="56" fill={ac} opacity="0.15" />
      <rect x="0" y="0" width={W} height="56"
        fill="url(#heroSheen)" opacity="0.2" />
      {/* hero text overlay */}
      <rect x="16" y="14" width="90" height="10" rx="3" fill="rgba(255,255,255,0.55)" />
      <rect x="16" y="28" width="60" height="6" rx="2" fill="rgba(255,255,255,0.3)" />
      <rect x="16" y="40" width="44" height="10" rx="4" fill={ac} opacity="0.7" />
      {/* nav overlay */}
      <rect width={W} height="18" fill="rgba(0,0,0,0.28)" />
      <rect x="10" y="5" width="28" height="7" rx="2" fill={ac} opacity="0.6" />
      {[0,1,2].map(i => (
        <rect key={i} x={120 + i*44} y="6" width="28" height="6" rx="2" fill="rgba(255,255,255,0.35)" />
      ))}
      {/* menu section label */}
      <rect x="10" y="64" width="48" height="6" rx="2" fill={ac} opacity="0.45" />
      <rect x="10" y="72" width="80" height="3" rx="1.5" fill={txt3} />
      {/* menu cards */}
      {[0,1,2].map(i => (
        <g key={i} transform={`translate(${8 + i * 90}, 82)`}>
          <rect width="82" height="68" rx="5" fill={surf} stroke={line} strokeWidth="0.6" />
          <rect x="0" y="0" width="82" height="34" rx="5"
            fill={i===1?ac:surf2} opacity={i===1?'0.25':'1'} />
          <rect x="8" y="40" width="52" height="6" rx="2" fill={txt} />
          <rect x="8" y="50" width="32" height="4" rx="2" fill={txt2} />
          <rect x="8" y="58" width="24" height="6" rx="2" fill={ac} opacity="0.45" />
        </g>
      ))}
      <defs>
        <linearGradient id="heroSheen" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="white" stopOpacity="0.4" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );

  if (type === 'portfolio') return (
    <svg {...p}>
      {/* top nav */}
      <rect width={W} height="20" fill={surf} />
      <rect x="10" y="6" width="32" height="8" rx="2" fill={ac} opacity="0.45" />
      {[0,1,2].map(i => (
        <rect key={i} x={160 + i*36} y="7" width="26" height="6" rx="2" fill={txt2} />
      ))}
      {/* hero: avatar + bio */}
      <circle cx="36" cy="60" r="24" fill={surf} stroke={ac} strokeWidth="1.2" strokeOpacity="0.45" />
      <circle cx="36" cy="54" r="10" fill={ac} opacity="0.28" />
      <ellipse cx="36" cy="76" rx="16" ry="8" fill={ac} opacity="0.12" />
      <rect x="72" y="32" width="100" height="11" rx="3" fill={txt} />
      <rect x="72" y="48" width="72" height="7" rx="2" fill={txt2} />
      <rect x="72" y="60" width="88" height="5" rx="2" fill={txt3} />
      <rect x="72" y="70" width="64" height="5" rx="2" fill={txt3} />
      <rect x="72" y="82" width="52" height="12" rx="4" fill={ac} opacity="0.4" />
      {/* skills row */}
      {[0,1,2,3].map(i => (
        <rect key={i} x={10 + i*52} y="108" width="44" height="8" rx="3"
          fill={i===0?ac:surf} opacity={i===0?'0.35':'1'} stroke={line} strokeWidth="0.5" />
      ))}
      {/* project tiles */}
      {[0,1,2].map(i => (
        <g key={i} transform={`translate(${8 + i * 90}, 124)`}>
          <rect width="82" height="30" rx="5" fill={surf} stroke={line} strokeWidth="0.6" />
          <rect x="6" y="7" width="40" height="6" rx="2"
            fill={i===1?ac:txt} opacity={i===1?'0.45':'1'} />
          <rect x="6" y="17" width="28" height="4" rx="2" fill={txt2} />
        </g>
      ))}
    </svg>
  );

  if (type === 'blog') return (
    <svg {...p}>
      {/* nav */}
      <rect width={W} height="20" fill={surf} />
      <rect x="10" y="6" width="36" height="8" rx="2" fill={ac} opacity="0.45" />
      <rect x="220" y="7" width="50" height="6" rx="2" fill={txt2} />
      {/* hero post */}
      <rect x="8" y="28" width={W-16} height="52" rx="5" fill={surf2} stroke={line} strokeWidth="0.6" />
      <rect x="8" y="28" width={W-16} height="52" rx="5" fill={ac} opacity="0.12" />
      <rect x="18" y="38" width="80" height="9" rx="3" fill="rgba(255,255,255,0.5)" />
      <rect x="18" y="52" width="56" height="6" rx="2" fill="rgba(255,255,255,0.28)" />
      <rect x="18" y="64" width="36" height="10" rx="3" fill={ac} opacity="0.55" />
      {/* post list */}
      {[0,1,2].map(i => (
        <g key={i} transform={`translate(8, ${88 + i * 24})`}>
          <rect width={W-16} height="20" rx="4" fill={surf} stroke={line} strokeWidth="0.5" />
          <rect x="6" y="4" width="28" height="12" rx="3"
            fill={i===0?ac:surf2} opacity={i===0?'0.3':'1'} />
          <rect x="42" y="5" width="80" height="6" rx="2" fill={txt} />
          <rect x="42" y="13" width="52" height="4" rx="2" fill={txt2} />
          <rect x={W-50} y="6" width="34" height="8" rx="3" fill={surf2} stroke={line} strokeWidth="0.4" />
        </g>
      ))}
    </svg>
  );

  if (type === 'landing') return (
    <svg {...p}>
      {/* nav */}
      <rect width={W} height="22" fill={surf} />
      <rect x="10" y="7" width="36" height="8" rx="2" fill={ac} opacity="0.5" />
      {[0,1,2].map(i => (
        <rect key={i} x={110 + i*44} y="8" width="32" height="6" rx="2" fill={txt2} />
      ))}
      <rect x="238" y="6" width="34" height="10" rx="4" fill={ac} opacity="0.45" />
      {/* hero section */}
      <rect x="0" y="22" width={W} height="72" fill={surf2} />
      <rect x="0" y="22" width={W} height="72" fill={ac} opacity="0.06" />
      <rect x="40" y="34" width="200" height="14" rx="4" fill={txt} />
      <rect x="60" y="52" width="160" height="8" rx="3" fill={txt2} />
      <rect x="80" y="66" width="60" height="16" rx="6" fill={ac} opacity="0.55" />
      <rect x="148" y="66" width="52" height="16" rx="6" fill={surf} stroke={line} strokeWidth="0.7" />
      {/* feature cards */}
      {[0,1,2].map(i => (
        <g key={i} transform={`translate(${8 + i * 90}, 102)`}>
          <rect width="82" height="50" rx="5" fill={surf} stroke={line} strokeWidth="0.6" />
          <rect x="8" y="8" width="16" height="16" rx="4"
            fill={ac} opacity={i===1?'0.5':'0.25'} />
          <rect x="8" y="30" width="52" height="6" rx="2" fill={txt} />
          <rect x="8" y="40" width="38" height="4" rx="2" fill={txt2} />
        </g>
      ))}
    </svg>
  );

  if (type === 'kanban') return (
    <svg {...p}>
      {/* top bar */}
      <rect width={W} height="22" fill={surf} />
      <rect x="10" y="7" width="48" height="8" rx="2" fill={ac} opacity="0.4" />
      <rect x="220" y="6" width="50" height="10" rx="4" fill={ac} opacity="0.3" />
      {/* 3 kanban columns */}
      {[0,1,2].map(ci => (
        <g key={ci} transform={`translate(${6 + ci * 92}, 28)`}>
          <rect width="86" height={H-34} rx="6" fill={surf2} stroke={line} strokeWidth="0.6" />
          {/* column header */}
          <rect x="6" y="6" width="52" height="7" rx="2"
            fill={ci===1?ac:txt2} opacity={ci===1?'0.5':'1'} />
          <rect x="62" y="5" width="18" height="9" rx="3" fill={surf} stroke={line} strokeWidth="0.4" />
          {/* cards in column */}
          {Array.from({length: ci===1?3:ci===0?2:1}).map((_, ri) => (
            <g key={ri} transform={`translate(6, ${20 + ri * 32})`}>
              <rect width="74" height="26" rx="4"
                fill={ci===1&&ri===0?ac:surf}
                opacity={ci===1&&ri===0?'0.22':'1'}
                stroke={ci===1&&ri===0?ac:line}
                strokeWidth={ci===1&&ri===0?'0.8':'0.5'} />
              <rect x="6" y="5" width={ri===0?48:36} height="5" rx="2" fill={txt} />
              <rect x="6" y="14" width={ri===0?32:24} height="4" rx="2" fill={txt2} />
              {ci===1&&ri===0 && <rect x="6" y="20" width="18" height="3" rx="1.5" fill={ac} opacity="0.5" />}
            </g>
          ))}
        </g>
      ))}
    </svg>
  );

  if (type === 'social') return (
    <svg {...p}>
      {/* nav */}
      <rect width={W} height="22" fill={surf} />
      <rect x="10" y="7" width="36" height="8" rx="2" fill={ac} opacity="0.45" />
      <rect x="116" y="8" width="48" height="6" rx="10" fill={surf2} stroke={line} strokeWidth="0.5" />
      <rect x="244" y="6" width="26" height="10" rx="5" fill={surf2} stroke={line} strokeWidth="0.5" />
      {/* story row */}
      {[0,1,2,3,4].map(i => (
        <g key={i} transform={`translate(${8 + i * 52}, 28)`}>
          <circle cx="20" cy="20" r="18"
            fill={surf2}
            stroke={i===0?ac:line}
            strokeWidth={i===0?'1.5':'0.6'} />
          <circle cx="20" cy="17" r="8" fill={i===0?ac:surf} opacity={i===0?'0.3':'1'} />
          <rect x="6" y="42" width="28" height="4" rx="2" fill={txt2} />
        </g>
      ))}
      {/* feed posts */}
      {[0,1].map(i => (
        <g key={i} transform={`translate(8, ${78 + i * 40})`}>
          <rect width={W-16} height="36" rx="5" fill={surf} stroke={line} strokeWidth="0.5" />
          <circle cx="18" cy="18" r="10" fill={i===0?ac:surf2} opacity={i===0?'0.35':'1'} />
          <rect x="34" y="8" width="100" height="6" rx="2" fill={txt} />
          <rect x="34" y="18" width="72" height="4" rx="2" fill={txt2} />
          <rect x="34" y="26" width="48" height="4" rx="2" fill={txt3} />
          <rect x={W-60} y="10" width="44" height="16" rx="4" fill={surf2} stroke={line} strokeWidth="0.4" />
        </g>
      ))}
    </svg>
  );

  // generic — code editor layout
  return (
    <svg {...p}>
      {/* title bar */}
      <rect width={W} height="18" fill={surf} />
      <circle cx="10" cy="9" r="4" fill="rgba(255,80,80,0.4)" />
      <circle cx="22" cy="9" r="4" fill="rgba(255,180,0,0.35)" />
      <circle cx="34" cy="9" r="4" fill="rgba(0,200,80,0.35)" />
      <rect x="90" y="5" width="100" height="8" rx="2" fill={surf2} stroke={line} strokeWidth="0.4" />
      {/* sidebar */}
      <rect x="0" y="18" width="48" height={H-18} fill={surf2} />
      {[0,1,2,3,4,5].map(i => (
        <rect key={i} x="8" y={26 + i * 16} width={i===0?32:i===2?20:26} height="6" rx="2"
          fill={i===0?ac:txt2} opacity={i===0?'0.4':'1'} />
      ))}
      {/* code lines */}
      {[
        [56, 0,  80, ac,   0.5],
        [56, 1,  52, txt,  1],
        [68, 2, 100, txt,  1],
        [68, 3,  72, txt2, 1],
        [68, 4,  88, txt2, 1],
        [56, 5,  40, ac,   0.35],
        [68, 6,  64, txt,  1],
        [68, 7,  48, txt2, 1],
        [56, 8,  32, txt3, 1],
      ].map(([indent, row, w, fill, op]) => (
        <rect key={row} x={indent} y={26 + row * 14} width={w} height="5" rx="2"
          fill={fill} opacity={op} />
      ))}
      {/* active line highlight */}
      <rect x="48" y={26 + 2*14 - 1} width={W-48} height="7" rx="1"
        fill={ac} opacity="0.06" />
    </svg>
  );
}

// ── Mini UI Preview ──────────────────────────────────────────────────────────
// Infers a layout type from the project name, renders a tiny SVG mockup.
// Pure frontend — no API, no new fields.

function inferPreviewType(name = '') {
  const n = name.toLowerCase();
  if (/shop|store|ecomm|product|cart|market|retail/.test(n))   return 'ecommerce';
  if (/dash|admin|analytic|metric|stat|report|crm/.test(n))    return 'dashboard';
  if (/restaurant|food|menu|cafe|bistro|dine|eat/.test(n))     return 'restaurant';
  if (/portfolio|profile|resume|cv|personal|about/.test(n))    return 'portfolio';
  if (/blog|article|news|post|cms|magazine|write/.test(n))     return 'blog';
  if (/land|saas|marketing|promo|hero|startup/.test(n))        return 'landing';
  if (/task|kanban|todo|project|board|plan|manage/.test(n))    return 'kanban';
  if (/chat|message|inbox|mail|social|feed/.test(n))           return 'social';
  return 'generic';
}

function MiniPreview({ name, gradient }) {
  const type = inferPreviewType(name);
  // accent colour sampled from the gradient string
  const accentMatch = gradient.match(/rgba\((\d+),(\d+),(\d+)/);
  const ac = accentMatch ? `rgb(${accentMatch[1]},${accentMatch[2]},${accentMatch[3]})` : '#6366f1';
  const dim = { w: 200, h: 88 };
  // shared palette
  const bg   = 'rgba(0,0,0,0)';
  const line = 'rgba(255,255,255,0.13)';
  const blk  = 'rgba(255,255,255,0.08)';
  const blk2 = 'rgba(255,255,255,0.05)';
  const txt  = 'rgba(255,255,255,0.22)';
  const txt2 = 'rgba(255,255,255,0.12)';

  const svgProps = {
    width: '100%', height: '100%',
    viewBox: `0 0 ${dim.w} ${dim.h}`,
    xmlns: 'http://www.w3.org/2000/svg',
    style: { display: 'block', position: 'absolute', inset: 0 },
    preserveAspectRatio: 'xMidYMid slice',
  };

  if (type === 'ecommerce') return (
    <svg {...svgProps}>
      {/* 3 product cards */}
      {[0,1,2].map(i => (
        <g key={i} transform={`translate(${14 + i * 60}, 10)`}>
          <rect width="50" height="62" rx="5" fill={blk} stroke={line} strokeWidth="0.6"/>
          <rect x="6" y="6" width="38" height="28" rx="3" fill={i===1 ? ac : blk2} opacity={i===1?'0.35':'1'}/>
          <rect x="8" y="40" width="28" height="4" rx="2" fill={txt}/>
          <rect x="8" y="48" width="18" height="3" rx="1.5" fill={txt2}/>
          <rect x="8" y="56" width="22" height="4" rx="2" fill={ac} opacity="0.5"/>
        </g>
      ))}
    </svg>
  );

  if (type === 'dashboard') return (
    <svg {...svgProps}>
      {/* stat cards row */}
      {[0,1,2].map(i => (
        <rect key={i} x={10 + i*62} y="8" width="54" height="22" rx="4"
          fill={i===0 ? ac : blk} opacity={i===0?'0.3':'1'} stroke={line} strokeWidth="0.6"/>
      ))}
      {/* bar chart */}
      {[28,18,38,24,42,16,34].map((h,i) => (
        <rect key={i} x={10 + i*14} y={88-10-h} width="10" height={h} rx="2"
          fill={i===4 ? ac : blk} opacity={i===4?'0.55':'1'}/>
      ))}
      {/* line chart area */}
      <polyline
        points="118,72 132,58 146,64 160,48 174,54 188,42"
        fill="none" stroke={ac} strokeWidth="1.5" strokeOpacity="0.6"
        strokeLinecap="round" strokeLinejoin="round"/>
      <polyline
        points="118,72 132,58 146,64 160,48 174,54 188,42 188,78 118,78"
        fill={ac} fillOpacity="0.08" stroke="none"/>
    </svg>
  );

  if (type === 'restaurant') return (
    <svg {...svgProps}>
      {/* header bar */}
      <rect x="10" y="8" width="180" height="14" rx="4" fill={blk} stroke={line} strokeWidth="0.6"/>
      <rect x="18" y="12" width="40" height="6" rx="2" fill={ac} opacity="0.45"/>
      <rect x="66" y="12" width="28" height="6" rx="2" fill={txt}/>
      <rect x="102" y="12" width="28" height="6" rx="2" fill={txt}/>
      {/* menu items */}
      {[0,1,2].map(i => (
        <g key={i} transform={`translate(10, ${30 + i*18})`}>
          <rect width="180" height="14" rx="3" fill={blk} stroke={line} strokeWidth="0.5"/>
          <rect x="6" y="3" width="20" height="8" rx="2" fill={i===0?ac:blk2} opacity={i===0?'0.35':'1'}/>
          <rect x="32" y="4" width="60" height="4" rx="2" fill={txt}/>
          <rect x="32" y="10" width="36" height="2.5" rx="1" fill={txt2}/>
          <rect x="148" y="4" width="24" height="6" rx="2" fill={ac} opacity="0.4"/>
        </g>
      ))}
    </svg>
  );

  if (type === 'portfolio') return (
    <svg {...svgProps}>
      {/* avatar */}
      <circle cx="36" cy="30" r="16" fill={blk} stroke={ac} strokeWidth="1" strokeOpacity="0.5"/>
      <circle cx="36" cy="26" r="7" fill={ac} opacity="0.3"/>
      <ellipse cx="36" cy="42" rx="11" ry="6" fill={ac} opacity="0.15"/>
      {/* name + title lines */}
      <rect x="62" y="18" width="70" height="6" rx="3" fill={txt}/>
      <rect x="62" y="28" width="48" height="4" rx="2" fill={txt2}/>
      <rect x="62" y="36" width="56" height="3" rx="1.5" fill={txt2}/>
      {/* project tiles */}
      {[0,1,2].map(i => (
        <rect key={i} x={10 + i*62} y="58" width="54" height="22" rx="4"
          fill={i===1?ac:blk} opacity={i===1?'0.25':'1'} stroke={line} strokeWidth="0.5"/>
      ))}
    </svg>
  );

  if (type === 'blog') return (
    <svg {...svgProps}>
      {/* hero image placeholder */}
      <rect x="10" y="8" width="180" height="32" rx="4" fill={blk} stroke={line} strokeWidth="0.6"/>
      <rect x="10" y="8" width="180" height="32" rx="4" fill={ac} opacity="0.12"/>
      {/* title */}
      <rect x="10" y="46" width="130" height="6" rx="3" fill={txt}/>
      <rect x="10" y="56" width="90" height="4" rx="2" fill={txt2}/>
      {/* meta row */}
      <circle cx="16" cy="70" r="4" fill={blk2}/>
      <rect x="24" y="67" width="40" height="3" rx="1.5" fill={txt2}/>
      <rect x="72" y="67" width="28" height="3" rx="1.5" fill={txt2}/>
    </svg>
  );

  if (type === 'landing') return (
    <svg {...svgProps}>
      {/* nav */}
      <rect x="10" y="8" width="180" height="10" rx="3" fill={blk} stroke={line} strokeWidth="0.5"/>
      <rect x="16" y="11" width="30" height="4" rx="2" fill={ac} opacity="0.5"/>
      <rect x="158" y="11" width="24" height="4" rx="2" fill={ac} opacity="0.4"/>
      {/* hero headline */}
      <rect x="30" y="26" width="140" height="8" rx="3" fill={txt}/>
      <rect x="50" y="38" width="100" height="5" rx="2" fill={txt2}/>
      {/* CTA button */}
      <rect x="70" y="50" width="60" height="14" rx="6" fill={ac} opacity="0.45"/>
      <rect x="82" y="54" width="36" height="5" rx="2" fill="rgba(255,255,255,0.5)"/>
    </svg>
  );

  if (type === 'kanban') return (
    <svg {...svgProps}>
      {/* 3 columns */}
      {['Todo','In Progress','Done'].map((_, ci) => (
        <g key={ci} transform={`translate(${8 + ci*64}, 8)`}>
          <rect width="58" height="72" rx="5" fill={blk} stroke={line} strokeWidth="0.6"/>
          <rect x="6" y="6" width="36" height="4" rx="2" fill={ci===1?ac:txt} opacity={ci===1?'0.5':'1'}/>
          {[0,1,2].slice(0, ci===1?3:ci===0?2:1).map(ri => (
            <rect key={ri} x="6" y={16+ri*18} width="46" height="13" rx="3"
              fill={ci===1&&ri===0?ac:blk2} opacity={ci===1&&ri===0?'0.25':'1'}
              stroke={line} strokeWidth="0.4"/>
          ))}
        </g>
      ))}
    </svg>
  );

  if (type === 'social') return (
    <svg {...svgProps}>
      {[0,1,2].map(i => (
        <g key={i} transform={`translate(10, ${6 + i*26})`}>
          <circle cx="10" cy="10" r="8" fill={i===0?ac:blk} opacity={i===0?'0.35':'1'} stroke={line} strokeWidth="0.5"/>
          <rect x="24" y="5" width="100" height="4" rx="2" fill={txt}/>
          <rect x="24" y="13" width="70" height="3" rx="1.5" fill={txt2}/>
          <rect x="148" y="6" width="30" height="12" rx="3" fill={blk2} stroke={line} strokeWidth="0.4"/>
        </g>
      ))}
    </svg>
  );

  // generic — abstract code/file layout
  return (
    <svg {...svgProps}>
      {/* sidebar */}
      <rect x="10" y="8" width="38" height="72" rx="4" fill={blk} stroke={line} strokeWidth="0.6"/>
      {[0,1,2,3,4].map(i => (
        <rect key={i} x="16" y={14+i*12} width={i===0?24:i===2?18:20} height="4" rx="2"
          fill={i===0?ac:txt2} opacity={i===0?'0.5':'1'}/>
      ))}
      {/* main content lines */}
      {[0,1,2,3,4,5].map(i => (
        <rect key={i} x="56" y={14+i*11} width={[80,60,90,50,70,40][i]} height="4" rx="2"
          fill={i===0?ac:i%2===0?txt:txt2} opacity={i===0?'0.4':'1'}/>
      ))}
    </svg>
  );
}

const CARD_GRADIENTS = [
  'linear-gradient(135deg, rgba(99,102,241,0.3) 0%, rgba(139,92,246,0.15) 100%)',
  'linear-gradient(135deg, rgba(16,185,129,0.3) 0%, rgba(6,182,212,0.15) 100%)',
  'linear-gradient(135deg, rgba(245,158,11,0.3) 0%, rgba(239,68,68,0.15) 100%)',
  'linear-gradient(135deg, rgba(236,72,153,0.3) 0%, rgba(139,92,246,0.15) 100%)',
  'linear-gradient(135deg, rgba(59,130,246,0.3) 0%, rgba(99,102,241,0.15) 100%)',
  'linear-gradient(135deg, rgba(20,184,166,0.3) 0%, rgba(16,185,129,0.15) 100%)',
];

export default function ProjectList({ onSelectProject, onNewProject, onUseTemplate, onPrefill, section = 'home' }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [mounted, setMounted] = useState(false);
  const [showRepoPicker, setShowRepoPicker] = useState(false);
  const [showAllProjects, setShowAllProjects] = useState(false);
  const typedText = useTypingAnimation();
  const PROJECTS_PER_ROW = 4;

  useEffect(() => {
    fetchProjects();
    requestAnimationFrame(() => setMounted(true));
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await authFetch('/api/projects');
      if (res.ok) setProjects(await res.json());
    } catch (e) {
      console.error('Failed to fetch projects', e);
    } finally {
      setLoading(false);
    }
  };

  const filtered = projects.filter(p =>
    p.name?.toLowerCase().includes(search.toLowerCase())
  );
  const isSearching = search.trim().length > 0;
  const visibleProjects = (showAllProjects || isSearching)
    ? filtered
    : filtered.slice(0, PROJECTS_PER_ROW);

  return (
    <>
      <style>{`
        /* ── keyframes ── */
        @keyframes blink    { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes pulseGlow{ 0%,100%{opacity:0.55} 50%{opacity:1} }
        @keyframes flowPulse{ 0%,100%{opacity:0.25} 50%{opacity:0.75} }
        @keyframes nodePulse{ 0%,100%{box-shadow:0 0 0 0 rgba(99,102,241,0)} 50%{box-shadow:0 0 14px 3px rgba(99,102,241,0.14)} }
        @keyframes cardIn   { from{opacity:0;transform:translateY(14px) scale(0.985)} to{opacity:1;transform:translateY(0) scale(1)} }

        /* ── page background ── */
        .pl-page {
          background: radial-gradient(ellipse at 30% 0%, rgba(99,102,241,0.07) 0%, transparent 60%),
                      radial-gradient(ellipse at 80% 20%, rgba(139,92,246,0.05) 0%, transparent 50%),
                      linear-gradient(180deg, #080b18 0%, #0b0e1f 40%, #090c1a 100%);
          min-height: 100vh;
        }
        .pl-page--home {
          height: calc(100vh - 90px);
          min-height: unset;
          overflow: hidden;
          justify-content: center;
          padding: 0 32px !important;
          gap: 24px !important;
        }
        [data-theme="light"] .pl-page {
          background: radial-gradient(ellipse at 30% 0%, rgba(139,92,246,0.06) 0%, transparent 55%),
                      radial-gradient(ellipse at 80% 20%, rgba(99,102,241,0.04) 0%, transparent 50%),
                      linear-gradient(180deg, #f5f4fb 0%, #f7f6fd 40%, #f4f3fa 100%);
        }

        /* ── workspace heading entrance ── */
        @keyframes wsHeadIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ── workspace heading block ── */
        .pl-ws-heading {
          display: flex; flex-direction: column; gap: 6px;
          animation: wsHeadIn 0.38s cubic-bezier(0.22,1,0.36,1) both;
        }
        .pl-ws-eyebrow {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 10px; font-weight: 700; letter-spacing: 0.1em;
          text-transform: uppercase; color: rgba(99,102,241,0.7);
        }
        .pl-ws-eyebrow-dot {
          width: 5px; height: 5px; border-radius: 50%;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          box-shadow: 0 0 6px rgba(99,102,241,0.7);
          animation: pulseGlow 2.8s ease-in-out infinite;
          flex-shrink: 0;
        }
        .pl-ws-title {
          font-size: 26px; font-weight: 800; margin: 0;
          letter-spacing: -0.04em; line-height: 1.1;
          background: linear-gradient(120deg, #e2e8f0 0%, #c7d2fe 45%, #a5b4fc 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        }
        .pl-ws-tagline {
          font-size: 12px; font-weight: 500; letter-spacing: 0.04em;
          color: rgba(148,163,184,0.6); margin: 0;
        }
        .pl-ws-tagline span { color: rgba(165,180,252,0.55); }
        .pl-ws-count {
          display: inline-flex; align-items: center; gap: 5px;
          margin-top: 2px; align-self: flex-start;
          padding: 3px 10px 3px 7px;
          background: rgba(99,102,241,0.1);
          border: 1px solid rgba(99,102,241,0.2);
          border-radius: 20px;
          font-size: 10px; font-weight: 700; letter-spacing: 0.05em;
          color: rgba(165,180,252,0.8); text-transform: uppercase;
        }
        .pl-ws-count-pip {
          width: 4px; height: 4px; border-radius: 50%;
          background: #818cf8; opacity: 0.8;
        }
        /* light theme overrides */
        [data-theme="light"] .pl-ws-eyebrow { color: rgba(79,70,229,0.65); }
        [data-theme="light"] .pl-ws-title {
          background: linear-gradient(120deg, #0f172a 0%, #3730a3 50%, #4f46e5 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        }
        [data-theme="light"] .pl-ws-tagline { color: rgba(71,85,105,0.7); }
        [data-theme="light"] .pl-ws-tagline span { color: rgba(79,70,229,0.5); }
        [data-theme="light"] .pl-ws-count {
          background: rgba(99,102,241,0.07);
          border-color: rgba(99,102,241,0.16);
          color: rgba(79,70,229,0.75);
        }
        [data-theme="light"] .pl-ws-count-pip { background: #6366f1; }

        /* ── projects section accent line ── */
        .pl-projects-section-wrap::before {
          content: ''; position: absolute; top: 0; left: 12%; right: 12%; height: 1px;
          background: linear-gradient(90deg, transparent, rgba(99,102,241,0.45), rgba(139,92,246,0.3), transparent);
          pointer-events: none;
        }
        [data-theme="light"] .pl-projects-section-wrap::before {
          background: linear-gradient(90deg, transparent, rgba(99,102,241,0.22), rgba(139,92,246,0.15), transparent);
        }

        /* ── featured project card ── */
        @keyframes featuredIn { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }

        .pl-featured {
          position: relative;
          display: flex; flex-direction: column;
          width: 100%;
          border-radius: 18px;
          cursor: pointer;
          overflow: hidden;
          animation: featuredIn 0.38s cubic-bezier(0.22,1,0.36,1) both;
          background: linear-gradient(155deg, #13162e 0%, #0e1128 100%);
          border: 1px solid rgba(148,163,184,0.1);
          box-shadow:
            0 1px 2px rgba(0,0,0,0.5),
            0 8px 32px rgba(0,0,0,0.36),
            inset 0 1px 0 rgba(255,255,255,0.06);
          transition: transform 0.22s cubic-bezier(0.22,1,0.36,1), box-shadow 0.22s ease, border-color 0.22s ease, background 0.22s ease;
          backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px);
        }
        /* 3px accent bar full-width across top */
        .pl-featured::before {
          content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
          background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #a78bfa 100%);
          border-radius: 18px 18px 0 0;
          pointer-events: none; z-index: 2;
        }
        /* subtle corner highlight — top-left radial glow */
        .pl-featured::after {
          content: ''; position: absolute; top: 0; left: 0;
          width: 180px; height: 120px;
          background: radial-gradient(ellipse at 0% 0%, rgba(99,102,241,0.13) 0%, transparent 70%);
          pointer-events: none; z-index: 1;
          border-radius: 18px 0 0 0;
          opacity: 0.7; transition: opacity 0.22s ease;
        }
        .pl-featured:hover::after { opacity: 1; }
        .pl-featured:hover {
          transform: translateY(-3px);
          background: linear-gradient(155deg, #161a36 0%, #111428 100%);
          border-color: rgba(148,163,184,0.18);
          box-shadow:
            0 2px 8px rgba(0,0,0,0.4),
            0 16px 40px rgba(0,0,0,0.3),
            0 0 0 1px rgba(99,102,241,0.12),
            inset 0 1px 0 rgba(255,255,255,0.08);
        }
        .pl-featured-info {
          display: flex; flex-direction: column; gap: 14px;
          padding: 28px 32px 26px;
        }
        .pl-featured-label {
          display: inline-flex; align-items: center; gap: 5px;
          font-size: 9px; font-weight: 700; letter-spacing: 0.13em;
          text-transform: uppercase; color: rgba(139,92,246,0.6);
        }
        .pl-featured-label-dot {
          width: 4px; height: 4px; border-radius: 50%;
          background: #818cf8;
          box-shadow: 0 0 5px rgba(129,140,248,0.7);
          animation: pulseGlow 2.4s ease-in-out infinite;
        }
        /* project name — dominant, high contrast */
        .pl-featured-name {
          font-size: 22px; font-weight: 800; margin: 0;
          letter-spacing: -0.04em; line-height: 1.2;
          color: #f0f4ff;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        /* meta row — technology + file count */
        .pl-featured-meta {
          display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
        }
        /* technology badge — coloured, readable */
        .pl-featured-badge {
          font-size: 10px; font-weight: 600;
          padding: 3px 9px; border-radius: 6px;
          text-transform: uppercase; letter-spacing: 0.07em;
          backdrop-filter: blur(6px);
        }
        /* file count + status — muted, secondary */
        .pl-featured-stat {
          font-size: 11px; font-weight: 500;
          color: rgba(148,163,184,0.55);
          display: flex; align-items: center; gap: 3px;
          letter-spacing: 0.01em;
        }
        .pl-featured-stat-sep {
          width: 3px; height: 3px; border-radius: 50%;
          background: rgba(148,163,184,0.35); flex-shrink: 0;
        }
        .pl-featured-footer {
          display: flex; align-items: center; justify-content: space-between;
          padding-top: 14px;
          border-top: 1px solid rgba(255,255,255,0.07);
        }
        /* last updated — quietest element */
        .pl-featured-date {
          font-size: 11px; font-weight: 400;
          color: rgba(148,163,184,0.4);
          letter-spacing: 0.01em;
        }
        .pl-featured-open {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 9px 20px 9px 16px;
          background: linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(139,92,246,0.11) 100%);
          border: 1px solid rgba(139,92,246,0.3);
          border-radius: 10px;
          color: #a5b4fc;
          font-size: 12px; font-weight: 700; letter-spacing: 0.04em;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 1px 4px rgba(0,0,0,0.18);
          transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease,
                      box-shadow 0.18s ease, transform 0.18s cubic-bezier(0.22,1,0.36,1);
          white-space: nowrap;
        }
        .pl-featured-open-icon {
          display: flex; align-items: center; justify-content: center;
          width: 18px; height: 18px; border-radius: 5px;
          background: rgba(139,92,246,0.2);
          border: 1px solid rgba(139,92,246,0.28);
          flex-shrink: 0;
          transition: background 0.18s ease, border-color 0.18s ease, transform 0.18s cubic-bezier(0.22,1,0.36,1);
        }
        .pl-featured:hover .pl-featured-open {
          background: linear-gradient(135deg, rgba(99,102,241,0.28) 0%, rgba(139,92,246,0.18) 100%);
          border-color: rgba(167,139,250,0.52);
          color: #c7d2fe;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.09), 0 2px 10px rgba(99,102,241,0.18);
          transform: translateY(-1px);
        }
        .pl-featured:hover .pl-featured-open-icon {
          background: rgba(139,92,246,0.32);
          border-color: rgba(167,139,250,0.45);
          transform: translateX(2px);
        }
        /* light theme — featured */
        [data-theme="light"] .pl-featured {
          background: #ffffff;
          border-color: rgba(99,102,241,0.16);
          box-shadow:
            0 1px 3px rgba(15,23,42,0.07),
            0 6px 24px rgba(15,23,42,0.06),
            inset 0 1px 0 rgba(255,255,255,0.9);
        }
        [data-theme="light"] .pl-featured::before {
          background: linear-gradient(90deg, #818cf8 0%, #a78bfa 50%, #c4b5fd 100%);
          opacity: 0.7;
        }
        [data-theme="light"] .pl-featured::after {
          background: radial-gradient(ellipse at 0% 0%, rgba(99,102,241,0.05) 0%, transparent 70%);
        }
        [data-theme="light"] .pl-featured:hover {
          background: #fafafe;
          border-color: rgba(99,102,241,0.28);
          box-shadow:
            0 2px 8px rgba(15,23,42,0.08),
            0 12px 32px rgba(99,102,241,0.08),
            0 0 0 1px rgba(99,102,241,0.08),
            inset 0 1px 0 rgba(255,255,255,0.9);
        }
        [data-theme="light"] .pl-featured:hover::before { opacity: 1; }
        [data-theme="light"] .pl-featured-name  { color: #0f172a; }
        [data-theme="light"] .pl-featured-label { color: rgba(79,70,229,0.6); }
        [data-theme="light"] .pl-featured-stat  { color: rgba(71,85,105,0.6); }
        [data-theme="light"] .pl-featured-date  { color: rgba(100,116,139,0.5); }
        [data-theme="light"] .pl-featured-footer { border-top-color: rgba(15,23,42,0.08); }
        [data-theme="light"] .pl-featured-open {
          background: rgba(99,102,241,0.06); border-color: rgba(99,102,241,0.18); color: #4f46e5;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.9), 0 1px 3px rgba(99,102,241,0.07);
        }
        [data-theme="light"] .pl-featured-open-icon { background: rgba(99,102,241,0.08); border-color: rgba(99,102,241,0.18); }
        [data-theme="light"] .pl-featured:hover .pl-featured-open { background: rgba(99,102,241,0.1); border-color: rgba(99,102,241,0.3); color: #3730a3; box-shadow: inset 0 1px 0 rgba(255,255,255,0.95), 0 2px 6px rgba(99,102,241,0.09); }
        [data-theme="light"] .pl-featured:hover .pl-featured-open-icon { background: rgba(99,102,241,0.14); border-color: rgba(99,102,241,0.28); }
        @media (max-width: 480px) {
          .pl-featured-info { padding: 20px 20px 18px; }
          .pl-featured-name { font-size: 17px; white-space: normal; }
          .pl-featured-open { gap: 6px !important; }
          .pl-featured-open-icon { width: 16px !important; height: 16px !important; }
          .pl-featured-stat-sep { display: none; }
        }

        /* ── project tile (non-featured) ── */
        .pl-project-card {
          position: relative;
          display: flex; flex-direction: row;
          overflow: hidden;
          border-radius: 16px;
          cursor: pointer;
          animation: cardIn 0.28s cubic-bezier(0.22,1,0.36,1) both;
          will-change: transform, box-shadow;
          background: linear-gradient(155deg, #13162e 0%, #0e1128 100%);
          border: 1px solid rgba(148,163,184,0.09);
          box-shadow:
            0 1px 2px rgba(0,0,0,0.5),
            0 4px 14px rgba(0,0,0,0.3),
            inset 0 1px 0 rgba(255,255,255,0.05);
          transition:
            transform 0.2s cubic-bezier(0.22,1,0.36,1),
            box-shadow 0.2s ease,
            border-color 0.2s ease,
            background 0.2s ease;
          backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
        }
        .pl-project-card:hover {
          transform: translateY(-2px);
          background: linear-gradient(155deg, #161a36 0%, #111428 100%);
          border-color: rgba(148,163,184,0.16);
          box-shadow:
            0 2px 6px rgba(0,0,0,0.4),
            0 8px 22px rgba(0,0,0,0.28),
            0 0 0 1px rgba(99,102,241,0.1),
            inset 0 1px 0 rgba(255,255,255,0.07);
        }
        .pl-project-card:active {
          transform: translateY(0);
          transition-duration: 0.08s;
        }
        /* 2px accent bar full-width across top */
        .pl-project-card::before {
          content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
          background: linear-gradient(90deg, rgba(99,102,241,0.5) 0%, rgba(139,92,246,0.35) 100%);
          border-radius: 16px 16px 0 0;
          pointer-events: none; opacity: 0.6;
          transition: opacity 0.2s ease;
        }
        .pl-project-card:hover::before { opacity: 1; }
        /* subtle corner highlight — top-left radial glow */
        .pl-project-card::after {
          content: ''; position: absolute; top: 0; left: 0;
          width: 100px; height: 70px;
          background: radial-gradient(ellipse at 0% 0%, rgba(99,102,241,0.1) 0%, transparent 70%);
          pointer-events: none; z-index: 1;
          border-radius: 16px 0 0 0;
          opacity: 0.5; transition: opacity 0.2s ease;
        }
        .pl-project-card:hover::after { opacity: 1; }
        /* info column — full width */
        .pl-tile-info {
          flex: 1; min-width: 0;
          display: flex; flex-direction: column; justify-content: space-between;
          padding: 16px 18px 15px;
        }
        /* project name — dominant */
        .pl-tile-name {
          font-size: 14px; font-weight: 700; margin: 0;
          color: #e8edf8; letter-spacing: -0.02em; line-height: 1.35;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          transition: color 0.18s ease;
        }
        .pl-project-card:hover .pl-tile-name { color: #f4f7ff; }
        /* meta row — technology + file count */
        .pl-tile-meta {
          display: flex; align-items: center; gap: 6px; margin-top: 7px; flex-wrap: wrap;
        }
        /* technology badge — pill, coloured, readable */
        .pl-tile-badge {
          font-size: 9px; font-weight: 600; letter-spacing: 0.07em; text-transform: uppercase;
          padding: 2px 7px; border-radius: 5px; backdrop-filter: blur(4px);
        }
        .pl-tile-dot {
          width: 3px; height: 3px; border-radius: 50%;
          background: rgba(148,163,184,0.32); flex-shrink: 0;
        }
        /* file count — secondary, muted */
        .pl-tile-files {
          font-size: 10px; font-weight: 500;
          color: rgba(148,163,184,0.45);
          letter-spacing: 0.01em;
        }
        .pl-tile-footer {
          display: flex; align-items: center; justify-content: space-between;
          margin-top: 10px; padding-top: 10px;
          border-top: 1px solid rgba(255,255,255,0.07);
          transition: border-color 0.2s ease;
        }
        .pl-project-card:hover .pl-tile-footer { border-top-color: rgba(255,255,255,0.12); }
        /* last updated — quietest element */
        .pl-tile-date {
          font-size: 10px; font-weight: 400;
          color: rgba(148,163,184,0.38);
          letter-spacing: 0.01em;
        }
        .pl-tile-open {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 4px 9px 4px 8px;
          font-size: 10px; font-weight: 700; letter-spacing: 0.05em;
          color: rgba(129,140,248,0.5);
          background: rgba(99,102,241,0.07);
          border: 1px solid rgba(99,102,241,0.14);
          border-radius: 6px;
          transition: color 0.18s ease, background 0.18s ease, border-color 0.18s ease,
                      box-shadow 0.18s ease, transform 0.18s cubic-bezier(0.22,1,0.36,1);
        }
        .pl-project-card:hover .pl-tile-open {
          color: #c7d2fe;
          background: rgba(99,102,241,0.16);
          border-color: rgba(139,92,246,0.35);
          box-shadow: 0 1px 6px rgba(99,102,241,0.14);
          transform: translateY(-1px);
        }
        /* light theme — tile */
        [data-theme="light"] .pl-project-card {
          background: #ffffff;
          border-color: rgba(99,102,241,0.14);
          box-shadow:
            0 1px 3px rgba(15,23,42,0.07),
            0 4px 14px rgba(15,23,42,0.05),
            inset 0 1px 0 rgba(255,255,255,0.9);
        }
        [data-theme="light"] .pl-project-card::before {
          background: linear-gradient(90deg, rgba(99,102,241,0.4) 0%, rgba(139,92,246,0.28) 100%);
        }
        [data-theme="light"] .pl-project-card::after {
          background: radial-gradient(ellipse at 0% 0%, rgba(99,102,241,0.05) 0%, transparent 70%);
        }
        [data-theme="light"] .pl-project-card:hover {
          background: #fafafe;
          border-color: rgba(99,102,241,0.26);
          box-shadow:
            0 2px 6px rgba(15,23,42,0.08),
            0 8px 20px rgba(99,102,241,0.07),
            0 0 0 1px rgba(99,102,241,0.07),
            inset 0 1px 0 rgba(255,255,255,0.9);
        }
        [data-theme="light"] .pl-tile-name { color: #111827; }
        [data-theme="light"] .pl-project-card:hover .pl-tile-name { color: #030712; }
        [data-theme="light"] .pl-tile-files { color: rgba(71,85,105,0.58); }
        [data-theme="light"] .pl-tile-footer { border-top-color: rgba(15,23,42,0.08); }
        [data-theme="light"] .pl-project-card:hover .pl-tile-footer { border-top-color: rgba(15,23,42,0.13); }
        [data-theme="light"] .pl-tile-date  { color: rgba(100,116,139,0.5); }
        [data-theme="light"] .pl-tile-open {
          color: rgba(79,70,229,0.55);
          background: rgba(99,102,241,0.05);
          border-color: rgba(99,102,241,0.14);
        }
        [data-theme="light"] .pl-project-card:hover .pl-tile-open {
          color: #3730a3;
          background: rgba(99,102,241,0.09);
          border-color: rgba(99,102,241,0.26);
          box-shadow: 0 1px 4px rgba(99,102,241,0.09);
        }

        /* ── search — hover before focus ── */
        .pl-search-wrap:hover:not(:focus-within) {
          border-color: rgba(99,102,241,0.35);
          box-shadow: 0 1px 8px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.05);
        }
        [data-theme="light"] .pl-search-wrap:hover:not(:focus-within) {
          border-color: rgba(139,92,246,0.3);
          box-shadow: 0 1px 6px rgba(139,92,246,0.1), inset 0 1px 0 rgba(255,255,255,1);
        }

        /* ── New Project button — add border-color to transition ── */
        .pl-new-project-btn {
          transition:
            transform 0.17s cubic-bezier(0.22,1,0.36,1),
            box-shadow 0.17s ease,
            filter 0.17s ease,
            border-color 0.17s ease !important;
        }
        .pl-new-project-btn:hover { border-color: rgba(192,168,255,0.55) !important; }
        [data-theme="light"] .pl-projects-section-wrap {
          background: rgba(255,255,255,0.82) !important;
          border-color: rgba(99,102,241,0.12) !important;
          box-shadow: 0 0 0 1px rgba(99,102,241,0.05), 0 4px 32px rgba(99,102,241,0.07), 0 1px 4px rgba(0,0,0,0.04) !important;
        }
        [data-theme="light"] .pl-show-all-btn {
          background: rgba(99,102,241,0.05) !important;
          border-color: rgba(99,102,241,0.18) !important;
          color: #4f46e5 !important;
        }
        [data-theme="light"] .pl-show-all-btn:hover {
          background: rgba(99,102,241,0.1) !important;
          border-color: rgba(99,102,241,0.32) !important;
        }
        [data-theme="light"] .pl-typing-bar {
          background: rgba(99,102,241,0.04) !important;
          border-color: rgba(99,102,241,0.12) !important;
        }
        [data-theme="light"] .pl-typing-static { color: #475569; }
        [data-theme="light"] .pl-wf-node {
          background: rgba(255,255,255,0.85) !important;
          border-color: rgba(99,102,241,0.1) !important;
          box-shadow: 0 1px 6px rgba(99,102,241,0.06) !important;
        }
        /* ── light: skeleton cards, empty state, hero sub, divider ── */
        [data-theme="light"] .pl-skeleton {
          background: #ffffff !important;
          border-color: rgba(139,92,246,0.14) !important;
          box-shadow: 0 1px 6px rgba(139,92,246,0.07) !important;
        }
        [data-theme="light"] .pl-empty-title { color: #475569; }
        [data-theme="light"] .pl-empty-hint  { color: #94a3b8; }
        [data-theme="light"] .pl-hero-sub    { color: #475569; }
        [data-theme="light"] .pl-divider {
          background: linear-gradient(90deg, transparent, rgba(139,92,246,0.18), transparent);
        }

        /* ── search field ── */
        .pl-search-wrap {
          position: relative; display: flex; align-items: center;
          border-radius: 11px;
          background: rgba(12,15,30,0.82);
          border: 1px solid rgba(99,102,241,0.18);
          box-shadow: 0 1px 6px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.04);
          transition: border-color 0.22s ease, box-shadow 0.22s ease;
        }
        .pl-search-wrap:focus-within {
          border-color: rgba(99,102,241,0.55);
          box-shadow: 0 0 0 3px rgba(99,102,241,0.12), 0 2px 12px rgba(99,102,241,0.15);
        }
        .pl-search-icon {
          position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
          width: 14px; height: 14px; pointer-events: none; flex-shrink: 0;
          color: rgba(129,140,248,0.4);
          transition: color 0.2s ease;
        }
        .pl-search-wrap:focus-within .pl-search-icon { color: rgba(129,140,248,0.95); }
        .pl-search {
          padding: 9px 12px 9px 36px !important;
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
          outline: none !important;
          color: #dde3f0;
          font-size: 13px; font-family: inherit;
          width: 210px;
          letter-spacing: 0.01em;
        }
        .pl-search::placeholder { color: rgba(148,163,184,0.28); }
        /* kbd hint */
        .pl-search-kbd {
          margin-right: 10px; flex-shrink: 0;
          padding: 2px 6px;
          border-radius: 5px;
          border: 1px solid rgba(99,102,241,0.18);
          background: rgba(99,102,241,0.08);
          font-size: 10px; font-weight: 600; letter-spacing: 0.04em;
          color: rgba(148,163,184,0.38);
          pointer-events: none;
          transition: opacity 0.2s ease;
        }
        .pl-search-wrap:focus-within .pl-search-kbd { opacity: 0; }
        /* light theme */
        [data-theme="light"] .pl-search-wrap {
          background: #ffffff;
          border-color: rgba(139,92,246,0.18);
          box-shadow: 0 1px 4px rgba(139,92,246,0.08), inset 0 1px 0 rgba(255,255,255,1);
        }
        [data-theme="light"] .pl-search-wrap:focus-within {
          border-color: rgba(99,102,241,0.45);
          box-shadow: 0 0 0 3px rgba(99,102,241,0.09), 0 2px 10px rgba(139,92,246,0.1);
        }
        [data-theme="light"] .pl-search { color: #1a1f36; }
        [data-theme="light"] .pl-search::placeholder { color: rgba(100,116,139,0.4); }
        [data-theme="light"] .pl-search-icon { color: rgba(99,102,241,0.35); }
        [data-theme="light"] .pl-search-wrap:focus-within .pl-search-icon { color: rgba(79,70,229,0.85); }
        [data-theme="light"] .pl-search-kbd {
          border-color: rgba(139,92,246,0.14);
          background: rgba(99,102,241,0.05);
          color: rgba(71,85,105,0.4);
        }

        /* ── New Project button ── */
        .pl-new-project-btn {
          position: relative; overflow: hidden;
          display: inline-flex; align-items: center; gap: 7px;
          padding: 9px 17px 9px 14px;
          background: linear-gradient(135deg, #5b5ef4 0%, #7c3aed 55%, #9333ea 100%);
          border: 1px solid rgba(167,139,250,0.4);
          border-radius: 11px;
          color: #fff;
          font-size: 13px; font-weight: 700; letter-spacing: 0.015em;
          cursor: pointer; white-space: nowrap; font-family: inherit;
          box-shadow:
            0 1px 0 rgba(255,255,255,0.12) inset,
            0 -1px 0 rgba(0,0,0,0.15) inset,
            0 3px 14px rgba(99,102,241,0.38),
            0 1px 4px rgba(0,0,0,0.22);
          transition:
            transform 0.17s cubic-bezier(0.22,1,0.36,1),
            box-shadow 0.17s ease,
            filter 0.17s ease;
        }
        /* top glass sheen */
        .pl-new-project-btn::after {
          content: '';
          position: absolute; top: 0; left: 0; right: 0; height: 46%;
          background: linear-gradient(180deg, rgba(255,255,255,0.16) 0%, transparent 100%);
          border-radius: 11px 11px 0 0;
          pointer-events: none;
        }
        .pl-new-project-btn:hover {
          transform: translateY(-2px);
          filter: brightness(1.1);
          box-shadow:
            0 1px 0 rgba(255,255,255,0.14) inset,
            0 -1px 0 rgba(0,0,0,0.15) inset,
            0 6px 24px rgba(99,102,241,0.52),
            0 2px 6px rgba(0,0,0,0.24);
        }
        .pl-new-project-btn:active {
          transform: translateY(0) scale(0.985);
          filter: brightness(0.96);
          box-shadow: 0 1px 6px rgba(99,102,241,0.28), 0 1px 3px rgba(0,0,0,0.2);
        }
        [data-theme="light"] .pl-new-project-btn {
          box-shadow:
            0 1px 0 rgba(255,255,255,0.22) inset,
            0 -1px 0 rgba(0,0,0,0.08) inset,
            0 2px 12px rgba(99,102,241,0.28),
            0 1px 3px rgba(99,102,241,0.12);
        }
        [data-theme="light"] .pl-new-project-btn:hover {
          box-shadow:
            0 1px 0 rgba(255,255,255,0.24) inset,
            0 -1px 0 rgba(0,0,0,0.08) inset,
            0 6px 22px rgba(99,102,241,0.38),
            0 2px 6px rgba(99,102,241,0.14);
        }

        /* ── legacy action-btn (typing bar buttons) ── */
        .pl-action-btn { transition: transform 0.18s cubic-bezier(0.22,1,0.36,1), box-shadow 0.18s ease, border-color 0.18s ease, background 0.18s ease !important; }
        .pl-action-btn:hover  { transform: translateY(-2px) !important; background: rgba(99,102,241,0.13) !important; border-color: rgba(99,102,241,0.52) !important; color: var(--accent) !important; box-shadow: 0 4px 14px rgba(99,102,241,0.14) !important; }
        .pl-action-btn:active { transform: translateY(0) !important; }
        .pl-import-btn:hover  { background: rgba(99,102,241,0.19) !important; border-color: rgba(99,102,241,0.62) !important; box-shadow: 0 4px 18px rgba(99,102,241,0.18) !important; }
        [data-theme="light"] .pl-action-btn { background: rgba(255,255,255,0.75) !important; border-color: rgba(99,102,241,0.2) !important; color: #4f46e5 !important; box-shadow: 0 1px 4px rgba(99,102,241,0.07) !important; }
        [data-theme="light"] .pl-action-btn:hover { background: rgba(255,255,255,0.98) !important; border-color: rgba(99,102,241,0.42) !important; box-shadow: 0 4px 14px rgba(99,102,241,0.1) !important; }
        [data-theme="light"] .pl-import-btn { background: rgba(99,102,241,0.06) !important; border-color: rgba(99,102,241,0.26) !important; }
        [data-theme="light"] .pl-import-btn:hover { background: rgba(99,102,241,0.11) !important; border-color: rgba(99,102,241,0.46) !important; }

        /* ── hero heading light theme ── */
        [data-theme="light"] .pl-hero-heading {
          background: linear-gradient(135deg, #1e1b4b 0%, #4338ca 60%, #6366f1 100%) !important;
          -webkit-background-clip: text !important;
          -webkit-text-fill-color: transparent !important;
        }
        [data-theme="light"] .pl-hero-badge {
          color: #4f46e5 !important;
          background: rgba(99,102,241,0.08) !important;
          border-color: rgba(99,102,241,0.2) !important;
        }

        /* ── tablet (641–860px) ── */
        @media (max-width: 860px) {
          .pl-featured-info { padding: 22px 24px; gap: 12px; }
          .pl-featured-name { font-size: 18px; }
        }

        /* ── responsive ── */
        @media (max-width: 700px) {
          .pl-projects-header { flex-direction: column !important; align-items: stretch !important; }
          .pl-projects-actions { width: 100%; padding-top: 0; }
          .pl-search-wrap { width: 100%; }
          .pl-search-wrap input { width: 100% !important; }
          .pl-action-row { flex-direction: column !important; }
          .pl-action-row button { width: 100% !important; justify-content: center; }
          .pl-wf-connector { display: none !important; }
        }
        @media (max-width: 480px) {
          .pl-hero-heading { font-size: 30px !important; }
          .pl-hero-sub     { font-size: 14px !important; }
          .pl-section-wrap { padding: 20px 16px 24px !important; }
          .pl-page         { padding: 32px 16px 80px !important; }
          .pl-projects-section-wrap { padding: 20px 16px 24px !important; border-radius: 16px !important; }
          .pl-featured-name { font-size: 15px !important; }
          .pl-featured-info { padding: 14px 16px !important; gap: 8px !important; }
          .pl-featured-open { padding: 6px 12px !important; font-size: 11px !important; }
        }
      `}</style>

      <div className={`pl-page${section === 'home' ? ' pl-page--home' : ''}`} style={{ ...s.page, opacity: mounted ? 1 : 0, transition: 'opacity 0.4s ease' }}>

        {/* Hero — home only */}
        {section === 'home' && <div style={s.hero}>
          <div style={s.glowOrb} />
          <div style={{ ...s.heroBadge, animation: 'pulseGlow 3s ease-in-out infinite' }} className="pl-hero-badge">
            ✨ AI-Powered Development
          </div>
          <h1 className="pl-hero-heading" style={s.heroHeading}>Code Your Imagination</h1>
          <p className="pl-hero-sub" style={s.heroSub}>Stop writing boilerplate, start building dreams</p>
        </div>}

        {/* AI Workflow — home only */}
        {section === 'home' && <div style={s.workflow}>
          {[
            { icon: <img src={messageIcon} alt="Describe" style={s.wfImg} />, label: 'Describe', sub: 'Turn your idea into a prompt' },
            { icon: <img src={codingIcon} alt="AI Builds" style={s.wfImg} />, label: 'AI Builds', sub: 'Agents generate full-stack code' },
            { icon: <img src={rocketIcon} alt="Preview & Deploy" style={s.wfImg} />, label: 'Preview & Deploy', sub: 'Test, iterate, and ship faster' },
          ].map((step, i, arr) => (
            <React.Fragment key={step.label}>
              <div className="pl-wf-node" style={s.wfNode}>
                <div style={s.wfIconRing}>{step.icon}</div>
                <div style={s.wfText}>
                  <div style={s.wfLabel}>{step.label}</div>
                  <div style={s.wfSub}>{step.sub}</div>
                </div>
              </div>
              {i < arr.length - 1 && (
                <div className="pl-wf-connector" style={s.wfConnector}>
                  <div style={s.wfLine} />
                  <div style={{ ...s.wfDot, animationDelay: `${i * 0.4}s` }} />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>}

        {/* Typing bar — home only */}
        {section === 'home' && (
          <div style={s.sectionHeader}>
            <span style={s.sectionLabel}>Start with your own idea</span>
          </div>
        )}

        {section === 'home' && (
        <div className="pl-typing-bar" style={s.typingBar}>
          <div style={s.typingRow}>
            <span className="pl-typing-static" style={s.typingStatic}>Ask Agent to create&nbsp;</span>
            <span style={s.typingDynamic}>{typedText}</span>
            <span style={s.typingCursor}>|</span>
          </div>

          {/* Action buttons row — always visible below the typing text */}
          <div className="pl-action-row" style={s.actionRow}>
            <button className="pl-action-btn" style={s.scratchBtn} onClick={onNewProject}>
              + Start from scratch
            </button>
            <button
              className="pl-action-btn pl-import-btn"
              style={s.importBtn}
              onClick={() => setShowRepoPicker(true)}
            >
              🔗 Import from GitHub / Bitbucket
            </button>
          </div>
        </div>
        )}

        {/* Template Gallery — templates tab only */}
        {section === 'templates' && <TemplateGallery onUseTemplate={onUseTemplate} />}

        {/* My Projects — projects tab only */}
        {section === 'projects' && (
        <div className="pl-projects-section-wrap" style={s.projectsSectionWrap}>
          <div className="pl-projects-header" style={s.projectsHeader}>
            <div className="pl-ws-heading">
              <span className="pl-ws-eyebrow">
                <span className="pl-ws-eyebrow-dot" />
                AI Development Environment
              </span>
              <h2 className="pl-ws-title">Your Workspace</h2>
              <p className="pl-ws-tagline">
                Build<span> · </span>Iterate<span> · </span>Ship
              </p>
              {!loading && (
                <span className="pl-ws-count">
                  <span className="pl-ws-count-pip" />
                  {projects.length} project{projects.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="pl-projects-actions" style={s.projectsActions}>
              <div className="pl-search-wrap">
                <svg className="pl-search-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="6.5" cy="6.5" r="4"/>
                  <line x1="9.8" y1="9.8" x2="14" y2="14"/>
                </svg>
                <input
                  className="pl-search"
                  placeholder="Search projects..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                <span className="pl-search-kbd">⌘K</span>
              </div>
              <button className="pl-new-project-btn" onClick={onNewProject}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <line x1="6" y1="1" x2="6" y2="11"/>
                  <line x1="1" y1="6" x2="11" y2="6"/>
                </svg>
                New Project
              </button>
            </div>
          </div>

          {loading ? (
            <div style={s.skeletonGrid}>
              {[1, 2, 3].map(i => (
                <div key={i} className="pl-skeleton" style={s.skeleton}>
                  <div style={s.skeletonThumb} />
                  <div style={s.skeletonBody}>
                    <div style={s.skeletonBar} />
                    <div style={{ ...s.skeletonBar, width: '55%', marginTop: 10 }} />
                    <div style={{ ...s.skeletonBar, width: '35%', marginTop: 8 }} />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div style={s.empty}>
              <span style={s.emptyIcon}>📂</span>
              <p className="pl-empty-title" style={s.emptyTitle}>
                {search ? `No projects matching "${search}"` : 'No projects yet'}
              </p>
              <p className="pl-empty-hint" style={s.emptyHint}>
                {search ? 'Try a different search term' : 'Pick an example above or start from scratch'}
              </p>
            </div>
          ) : (
            <>
              <div style={s.projectGrid}>
                {visibleProjects.map((p, idx) => {
                  const fw = frameworkStyle(p.framework);
                  const gradient = CARD_GRADIENTS[idx % CARD_GRADIENTS.length];

                  /* ── Featured card: first project when not searching ── */
                  if (idx === 0 && !isSearching) return (
                    <div
                      key={p.project_id}
                      className="pl-featured"
                      style={{ gridColumn: '1 / -1' }}
                      onClick={() => onSelectProject(p.project_id)}
                    >
                      {/* info */}
                      <div className="pl-featured-info">
                        <span className="pl-featured-label">
                          <span className="pl-featured-label-dot" />
                          Most Recent
                        </span>

                        <h3 className="pl-featured-name">{p.name}</h3>

                        <div className="pl-featured-meta">
                          <span
                            className="pl-featured-badge"
                            style={{ background: fw.bg, color: fw.color, border: `1px solid ${fw.color}28` }}
                          >
                            {p.framework || 'react'}
                          </span>
                          <span className="pl-featured-stat-sep" />
                          <span className="pl-featured-stat">📄 {p.file_count || 0} files</span>
                          {p.status && <>
                            <span className="pl-featured-stat-sep" />
                            <span className="pl-featured-stat" style={{ color: p.status === 'done' ? '#22c55e' : 'var(--text-muted)' }}>
                              {p.status}
                            </span>
                          </>}
                        </div>

                        <div className="pl-featured-footer">
                          <span className="pl-featured-date">🕐 {timeAgo(p.updated_at)}</span>
                          <span className="pl-featured-open">
                            Open Project
                            <span className="pl-featured-open-icon">
                              <svg width="9" height="9" viewBox="0 0 9 9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="1.5" y1="4.5" x2="7.5" y2="4.5"/>
                                <polyline points="4.5,1.5 7.5,4.5 4.5,7.5"/>
                              </svg>
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  );

                  /* ── Regular tiles ── */
                  return (
                    <div
                      key={p.project_id}
                      className="pl-project-card"
                      style={{ animationDelay: `${idx * 0.05}s` }}
                      onClick={() => onSelectProject(p.project_id)}
                    >
                      {/* info */}
                      <div className="pl-tile-info">
                        <div>
                          <h3 className="pl-tile-name">{p.name}</h3>
                          <div className="pl-tile-meta">
                            <span
                              className="pl-tile-badge"
                              style={{ background: fw.bg, color: fw.color, border: `1px solid ${fw.color}22` }}
                            >
                              {p.framework || 'react'}
                            </span>
                            <span className="pl-tile-dot" />
                            <span className="pl-tile-files">{p.file_count || 0} files</span>
                          </div>
                        </div>
                        <div className="pl-tile-footer">
                          <span className="pl-tile-date">{timeAgo(p.updated_at)}</span>
                          <span className="pl-tile-open">
                            Open
                            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="1" y1="4" x2="7" y2="4"/>
                              <polyline points="4,1 7,4 4,7"/>
                            </svg>
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Show All / Show Less toggle — hidden during search */}
              {!isSearching && filtered.length > PROJECTS_PER_ROW && (
                <button
                  className="pl-action-btn pl-show-all-btn"
                  style={s.showAllBtn}
                  onClick={() => setShowAllProjects(v => !v)}
                >
                  {showAllProjects
                    ? 'Show less ↑'
                    : `Show all ${filtered.length} projects ↓`}
                </button>
              )}
            </>
          )}
        </div>
        )}

      </div>

      {showRepoPicker && (
        <RepoPickerModal
          onSelect={(data) => {
            setShowRepoPicker(false);
            onSelectProject(data.project_id);
          }}
          onCancel={() => setShowRepoPicker(false)}
        />
      )}
    </>
  );
}

const s = {
  page: {
    width: '100%', maxWidth: 1100, margin: '0 auto', padding: '40px 32px 40px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 28,
  },
  hero: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 12, textAlign: 'center', position: 'relative', width: '100%', maxWidth: 760,
  },
  glowOrb: {
    position: 'absolute', top: -80, left: '50%', transform: 'translateX(-50%)',
    width: 480, height: 200, pointerEvents: 'none',
    background: 'radial-gradient(ellipse, rgba(99,102,241,0.14) 0%, transparent 70%)',
  },
  heroBadge: {
    fontSize: 11, fontWeight: 700, color: '#818cf8',
    background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.22)',
    padding: '5px 14px', borderRadius: 20, letterSpacing: '0.06em',
    textTransform: 'uppercase', display: 'inline-block',
  },
  heroHeading: {
    fontSize: 38, fontWeight: 800, margin: 0, lineHeight: 1.12, letterSpacing: '-0.03em',
    background: 'var(--hero-gradient, linear-gradient(135deg, #e2e8f0 0%, #a5b4fc 100%))',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  heroSub: { fontSize: 15, color: '#94a3b8', margin: 0, fontWeight: 500, maxWidth: 440 },

  // ── Typing bar: column layout so buttons always show below the text ──
  typingBar: {
    display: 'flex', flexDirection: 'column', gap: 10,
    background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.14)',
    borderRadius: 12, padding: '12px 18px', width: '100%', maxWidth: 760,
  },
  typingRow: {
    display: 'flex', alignItems: 'center', flexWrap: 'wrap',
    fontSize: 15, fontWeight: 500, lineHeight: 1.5,
  },
  typingStatic: { color: '#94a3b8', whiteSpace: 'nowrap' },
  typingDynamic: { color: 'var(--accent)', fontWeight: 600, minWidth: 2 },
  typingCursor: {
    color: '#6366f1', fontWeight: 300, fontSize: 18,
    animation: 'blink 1s step-end infinite', marginLeft: 1,
  },
  actionRow: {
    display: 'flex', gap: 10, flexWrap: 'wrap',
  },
  scratchBtn: {
    padding: '9px 18px',
    background: 'rgba(20,24,38,0.65)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    color: 'var(--accent)',
    border: '1px solid rgba(99,102,241,0.28)',
    borderRadius: 10,
    fontWeight: 600, cursor: 'pointer', fontSize: 13,
    boxShadow: '0 2px 10px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.05)',
    letterSpacing: '0.01em',
  },
  importBtn: {
    padding: '9px 18px',
    background: 'rgba(99,102,241,0.12)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    color: 'var(--accent)',
    border: '1px solid rgba(99,102,241,0.35)',
    borderRadius: 10,
    fontWeight: 600, cursor: 'pointer', fontSize: 13,
    display: 'flex', alignItems: 'center', gap: 6,
    boxShadow: '0 2px 10px rgba(99,102,241,0.1), inset 0 1px 0 rgba(255,255,255,0.06)',
    letterSpacing: '0.01em',
  },

  workflow: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: '100%', maxWidth: 760, flexWrap: 'wrap',
  },
  wfNode: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 8, padding: '14px 22px', flex: '1 1 140px', maxWidth: 200,
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 16, backdropFilter: 'blur(8px)',
    animation: 'nodePulse 4s ease-in-out infinite',
  },
  wfIconRing: { width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  wfImg: { width: 44, height: 44, objectFit: 'contain', filter: 'var(--icon-filter, brightness(0) invert(1))' },
  wfText: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  wfLabel: { fontSize: 13, fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.01em' },
  wfSub: { fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.5, maxWidth: 130 },
  wfConnector: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    position: 'relative', width: 80, flexShrink: 0, height: 2,
  },
  wfLine: {
    width: '100%', height: 1,
    background: 'linear-gradient(90deg, rgba(99,102,241,0.15), rgba(139,92,246,0.35), rgba(99,102,241,0.15))',
  },
  wfDot: {
    position: 'absolute', width: 6, height: 6, borderRadius: '50%',
    background: '#818cf8', boxShadow: '0 0 8px rgba(129,140,248,0.8)',
    animation: 'flowPulse 2s ease-in-out infinite',
  },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', maxWidth: 760 },
  sectionLabel: { fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: '0.1em', textTransform: 'uppercase' },
  sectionHint: { fontSize: 12, color: 'var(--text-muted)' },
  promptGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16, width: '100%' },
  promptCard: {
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: 14, padding: '24px 22px', cursor: 'pointer',
    transition: 'all 0.22s ease', display: 'flex', flexDirection: 'column', gap: 10,
    boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
  },
  promptCardLoading: { opacity: 0.55, cursor: 'wait', pointerEvents: 'none' },
  promptIconWrap: { fontSize: 30 },
  promptTitle: { fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', margin: 0 },
  promptDesc: { fontSize: 12, color: 'var(--text-muted)', margin: 0, lineHeight: 1.6, flexGrow: 1 },
  promptCta: { fontSize: 12, fontWeight: 600, color: '#818cf8', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 },
  spinner: { display: 'inline-block', animation: 'spin 1s linear infinite', fontSize: 14 },
  divider: { width: '100%', height: 1, background: 'linear-gradient(90deg, transparent, var(--border), transparent)' },
  sectionLabel: { fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase' },
  sectionHint: { fontSize: 12, color: '#475569' },

  divider: { width: '100%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(99,102,241,0.2), transparent)' },

  // ── My Projects — AI workspace panel ──────────────────────────────────────
  projectsSectionWrap: {
    width: '100%',
    background: 'rgba(15,18,32,0.62)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    border: '1px solid rgba(99,102,241,0.16)',
    borderRadius: 24,
    padding: '32px 32px 36px',
    boxShadow: '0 0 0 1px rgba(99,102,241,0.06), 0 8px 48px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.06)',
    display: 'flex', flexDirection: 'column', gap: 24,
    position: 'relative', overflow: 'hidden',
  },
  projectsSection: { width: '100%', display: 'flex', flexDirection: 'column', gap: 20 },
  projectsHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 },
  projectsActions: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', paddingTop: 4 },
  skeletonGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 },
  skeleton: {
    background: 'rgba(22,26,46,0.85)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(99,102,241,0.18)',
    borderRadius: 18, overflow: 'hidden',
    boxShadow: '0 2px 16px rgba(0,0,0,0.22)',
  },
  skeletonThumb: {
    height: 76,
    background: 'linear-gradient(90deg, rgba(99,102,241,0.08) 0%, rgba(139,92,246,0.12) 50%, rgba(99,102,241,0.08) 100%)',
    backgroundSize: '200% 100%', animation: 'shimmer 1.8s infinite',
  },
  skeletonBody: { padding: '16px 18px 20px' },
  skeletonBar: {
    height: 11, borderRadius: 6, width: '78%',
    background: 'rgba(99,102,241,0.1)',
    backgroundSize: '200% 100%', animation: 'shimmer 1.8s infinite',
  },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '56px 0', textAlign: 'center' },
  emptyIcon: { fontSize: 44, opacity: 0.18 },
  emptyTitle: { fontSize: 15, fontWeight: 600, color: '#94a3b8', margin: 0 },
  emptyHint: { fontSize: 13, color: '#64748b', margin: 0 },
  projectGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 },
  projectCard: {}, // visual handled by .pl-project-card CSS class
  cardThumb: {}, cardThumbPreview: {}, cardThumbSheen: {},
  cardBody: {}, projectName: {}, projectMeta: {}, badge: {}, fileCount: {},
  projectFooter: {}, projectDate: {}, openHint: {},
  showAllBtn: {
    marginTop: 4,
    padding: '11px 18px',
    background: 'rgba(99,102,241,0.08)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(99,102,241,0.22)',
    borderRadius: 10,
    color: '#a5b4fc',
    fontSize: 12, fontWeight: 600, letterSpacing: '0.03em',
    cursor: 'pointer', transition: 'all 0.18s',
    width: '100%',
  },
};
